import { useEffect, useState, useCallback, useRef } from "react";
import { Upload, Download, Trash2, FileSpreadsheet, FileText, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { api, errMsg, downloadFile } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card } from "../components/Card";
import { DataTable } from "../components/DataTable";

const fmt = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function Files() {
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => api.get("/files").then((r) => setRows(r.data)), []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) return <Card data-testid="files-forbidden"><p className="flex items-center gap-2 text-slate-600"><Lock className="h-4 w-4" />{t("onlyAdmin")}</p></Card>;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post("/files/upload", fd); toast.success(t("saved")); load(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const safe = (p) => p.catch((e) => toast.error(errMsg(e)));

  const cols = [
    { key: "filename", label: t("name"), render: (r) => <span className="font-medium text-slate-900" dir="auto">{r.filename}</span> },
    { key: "size", label: t("size"), render: (r) => <span className="font-mono text-xs">{fmt(r.size)}</span> },
    { key: "uploaded_by", label: t("uploadedBy"), render: (r) => <bdi className="text-xs">{r.uploaded_by}</bdi> },
    { key: "created_at", label: t("time"), render: (r) => <span className="font-mono text-xs" dir="ltr">{new Date(r.created_at).toLocaleString()}</span> },
    { key: "act", label: t("actions"), render: (r) => (
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" data-testid={`download-file-${r.id}`} onClick={() => safe(downloadFile(`/files/${r.id}/download`, r.filename))} aria-label={t("download")}><Download className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" data-testid={`delete-file-${r.id}`} onClick={() => safe(api.delete(`/files/${r.id}`).then(load))} aria-label={t("delete")}><Trash2 className="h-4 w-4 text-rose-700" /></Button>
      </div>) },
  ];

  return (
    <div>
      <PageHeader title={t("nav_files")} sub={t("onlyAdmin")}>
        <input ref={input} type="file" className="hidden" onChange={upload} data-testid="file-upload-input" />
        <Button data-testid="file-upload-button" disabled={busy} onClick={() => input.current?.click()} className="rounded-xl bg-slate-900"><Upload className="h-4 w-4" />{t("upload")}</Button>
      </PageHeader>
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <Card className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3"><FileSpreadsheet className="h-8 w-8 text-emerald-700" /><div><p className="font-semibold">1,000 Use Cases.xlsx</p><p className="text-xs text-slate-500">Use Cases · Taxonomy · Schema</p></div></div>
          <Button data-testid="download-xlsx-button" variant="outline" className="rounded-xl" onClick={() => safe(downloadFile("/export/use-cases.xlsx", "GRC_Task_Manager_1000_Use_Cases.xlsx"))}><Download className="h-4 w-4" /></Button>
        </Card>
        <Card className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3"><FileText className="h-8 w-8 text-sky-700" /><div><p className="font-semibold">Implementation Guide.docx</p><p className="text-xs text-slate-500">Base44 build prompt</p></div></div>
          <Button data-testid="download-docx-button" variant="outline" className="rounded-xl" onClick={() => safe(downloadFile("/export/build-prompt.docx", "Base44_GRC_Task_Manager_Implementation_Guide.docx"))}><Download className="h-4 w-4" /></Button>
        </Card>
      </div>
      <DataTable cols={cols} rows={rows} testId="files-table" />
    </div>
  );
}
